import { CommonModule } from '@angular/common';
import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { Industry, IndustryFlat } from '../../../models/industry';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-industry-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    NgxSpinnerModule,
    MatTreeModule
  ],
  templateUrl: './industry-list.component.html',
  styleUrl: './industry-list.component.scss'
})
export class IndustryListComponent implements OnInit {
  treeControl = new NestedTreeControl<Industry>(node => node.children);
  dataSource = new MatTreeNestedDataSource<Industry>();

  industries: Industry[] = [];

  selectedNode: Industry | null = null;

  // Edit state management
  editingItemId: string | null = null;
  editingValue: string = '';

  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;

  // Store expanded node IDs to preserve tree state
  private expandedNodeIds: Set<string> = new Set();

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadIndustries();
  }

  loadIndustries(preserveExpandedState: boolean = false): void {
    this.spinnerService.show();
    this.lutService.getIndustries().subscribe({
      next: (data) => {
        // Convert flat API response to hierarchical structure
        let hierarchicalData = Helpers.convertFlatToHierarchical(data);
        // Sort alphabetically at all levels
        this.industries = Helpers.sortIndustriesAlphabetically(hierarchicalData);
        this.dataSource.data = this.industries;
        this.treeControl.dataNodes = this.industries;

        if (preserveExpandedState) {
          // Restore the previously expanded state
          this.restoreExpandedState();
        } else {
          // Collapse all nodes by default on initial load
          this.treeControl.collapseAll();
        }

        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading industries:', error);
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to load Industries:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemSelected(industry: Industry): void {
  }

  onCreateIndustry(): void {
    // Save expanded state before modifications
    this.saveExpandedState();

    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newIndustry: Industry = {
      id: tempId,
      name: 'New industry',
      systemReserved: false
    };

    let parentId: string | null = null;
    let parentName: string | null = null;
    let parentNode: Industry | null = null;

    if (this.selectedNode) {
      // Find the current node in the active data structure
      const currentNode = this.findNodeById(this.selectedNode.id, this.industries);
      if (currentNode) {
        parentId = currentNode.id;
        parentName = currentNode.name;
        parentNode = currentNode;
        // Add as child to the current node
        if (!currentNode.children) {
          currentNode.children = [];
        }
        currentNode.children.push(newIndustry);
        // Expand the parent node to show the new child
        this.treeControl.expand(currentNode);
        // Also add parent to expanded nodes
        this.expandedNodeIds.add(currentNode.id);
      } else {
        console.warn("Selected node not found in current data structure, adding to root");
        this.industries.push(newIndustry);
      }
    } else {
      // Add to the industries array at root level
      this.industries.push(newIndustry);
    }

    // Sort the tree alphabetically at all levels
    this.industries = Helpers.sortIndustriesAlphabetically(this.industries);

    // Update the data source - force refresh
    this.dataSource.data = this.industries;
    this.treeControl.dataNodes = this.industries;
    this.dataSource.data = [...this.industries];

    // Restore expanded state
    this.restoreExpandedState();

    // Find and expand to the new node
    this.expandToNewNode(newIndustry.id, this.industries);

    // Call backend API
    this.spinnerService.show();
    const newIndustryData: Omit<IndustryFlat, 'id'> = {
      name: 'New industry',
      parentId: parentId,
      parentName: parentName
    };
    this.lutService.createIndustry(newIndustryData).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Industry created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('industries', this.industries);
        // Reload data from server to get the correct ID and structure
        this.loadIndustries(true); // Pass true to indicate we should preserve expanded state
      },
      error: (error) => {
        console.error('Error creating industry:', error);
        this.spinnerService.hide();
        // Remove the temp item on error
        if (parentNode) {
          parentNode.children = parentNode.children?.filter(c => c.id !== tempId);
        } else {
          this.industries = this.industries.filter(i => i.id !== tempId);
        }
        // Re-sort after removal
        this.industries = Helpers.sortIndustriesAlphabetically(this.industries);
        // Force tree refresh
        this.dataSource.data = this.industries;
        this.treeControl.dataNodes = this.industries;
        this.dataSource.data = [...this.industries];
        // Restore expanded state
        this.restoreExpandedState();
        this.toast.error(`<strong>Failed to create Industry:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });

    this.selectedNode = null;
  }

  selectNode(node: Industry): void {
    if (this.selectedNode && this.selectedNode.id === node.id) {
      this.selectedNode = null;
    } else {
      this.selectedNode = node;
    }
  }

  getName(node: Industry): string {
    return node.name || '(empty)';
  }

  getId(node: Industry): string {
    return node.id;
  }

  hasChild = (_: number, node: Industry) => !!node.children && node.children.length > 0;

  // Method to expand tree to show the newly added node
  expandToNewNode(nodeId: string, data: Industry[]): void {
    const findAndExpandPath = (nodes: Industry[], targetId: string, path: Industry[] = []): boolean => {
      for (const node of nodes) {
        const currentPath = [...path, node];

        if (node.id === targetId) {
          // Found the target node, expand all parents in the path
          for (const parentNode of path) {
            this.treeControl.expand(parentNode);
          }
          return true;
        }

        if (node.children && findAndExpandPath(node.children, targetId, currentPath)) {
          return true;
        }
      }
      return false;
    };

    findAndExpandPath(data, nodeId);
  }

  // Edit functionality methods
  isEditing(node: Industry): boolean {
    return this.editingItemId === this.getId(node);
  }

  startEdit(node: Industry, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.editingItemId = this.getId(node);
    this.editingValue = this.getName(node);

    // Focus the input after the view updates
    setTimeout(() => {
      try {
        if (this.editInput && this.editInput.nativeElement) {
          this.editInput.nativeElement.focus();
          this.editInput.nativeElement.select();
        }
      } catch (error) {
        console.warn('Could not focus edit input:', error);
      }
    }, 0);
  }

  saveEdit(node: Industry): void {
    if (this.editingValue.trim() && this.editingValue.trim() !== this.getName(node)) {
      const oldName = node.name;
      // Save expanded state before modifications
      this.saveExpandedState();
      // Update the node name optimistically
      this.updateNodeInTree(node, this.editingValue.trim());

      // Find parent information for the API call
      const flatData = Helpers.convertHierarchicalToFlat(this.industries);
      const nodeData = flatData.find(item => item.id === node.id);

      // Prepare the updated industry object for API
      const updatedIndustry: IndustryFlat = {
        id: node.id,
        name: this.editingValue.trim(),
        parentId: nodeData?.parentId || null,
        parentName: nodeData?.parentName || null
      };

      // Call backend API
      this.spinnerService.show();
      this.lutService.updateIndustry(node.id, updatedIndustry).subscribe({
        next: (response) => {
          this.spinnerService.hide();
          this.toast.success('Industry updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('industries', this.industries);
          // Update with server response
          this.updateNodeInTree(node, response.name);
        },
        error: (error) => {
          console.error('Error updating industry:', error);
          this.spinnerService.hide();
          // Rollback to original name on error
          this.updateNodeInTree(node, oldName);
          this.toast.error(`<strong>Failed to update Industry:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
    this.cancelEdit();
  }

  cancelEdit(): void {
    this.editingItemId = null;
    this.editingValue = '';
  }

  onEditKeyDown(event: KeyboardEvent, node: Industry): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEdit(node);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  onEditInputClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  private updateNodeInTree(targetNode: Industry, newName: string): void {
    const updateNode = (nodes: Industry[]): boolean => {
      for (const node of nodes) {
        if (this.getId(node) === this.getId(targetNode)) {
          node.name = newName;
          return true;
        }
        if (node.children && updateNode(node.children)) {
          return true;
        }
      }
      return false;
    };

    updateNode(this.industries);
    // Sort the tree alphabetically at all levels after update
    this.industries = Helpers.sortIndustriesAlphabetically(this.industries);
    // Update the data source to reflect changes - force refresh
    this.dataSource.data = this.industries;
    this.treeControl.dataNodes = this.industries;
    this.dataSource.data = [...this.industries];
    // Restore expanded state
    this.restoreExpandedState();
  }

  openDeleteConfirmation(industry: Industry): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(industry.name)
      .subscribe(result => {
        if (result) {
          // Proceed with delete operation
        }
      });
  }

  onEditIndustry(industry: Industry): void {
    this.startEdit(industry);
  }

  onDeleteIndustry(industry: Industry): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(industry.name)
      .subscribe(result => {
        if (result) {
          // Save expanded state before modifications
          this.saveExpandedState();
          // Store original tree for rollback in case of error
          const originalIndustries = JSON.parse(JSON.stringify(this.industries));

          // 1. Optimistically remove from local state immediately
          this.deleteIndustryFromTree(industry.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteIndustry(industry.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Industry deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('industries', this.industries);
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting industry:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original tree and re-sort
              this.industries = Helpers.sortIndustriesAlphabetically(originalIndustries);
              // Force tree refresh
              this.dataSource.data = this.industries;
              this.treeControl.dataNodes = this.industries;
              this.dataSource.data = [...this.industries];
              // Restore expanded state
              this.restoreExpandedState();
              this.toast.error(`<strong>Failed to delete Industry:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  // Method to delete an industry from anywhere in the tree
  deleteIndustryFromTree(industryId: string): void {
    // Find the parent node before deletion
    const parentNode = this.findParentNode(industryId, this.industries);

    const deleteFromNodes = (nodes: Industry[]): Industry[] => {
      const result: Industry[] = [];

      for (const node of nodes) {
        if (node.id === industryId) {
          // Skip this node (delete it)
          continue;
        }

        // Create a new node object to ensure change detection
        const newNode: Industry = {
          id: node.id,
          name: node.name,
          systemReserved: node.systemReserved
        };

        // Recursively process children
        if (node.children && node.children.length > 0) {
          const filteredChildren = deleteFromNodes(node.children);
          if (filteredChildren.length > 0) {
            newNode.children = filteredChildren;
          }
        }

        result.push(newNode);
      }

      return result;
    };

    // Delete from the tree structure
    this.industries = deleteFromNodes(this.industries);

    // Sort the tree alphabetically at all levels after deletion
    this.industries = Helpers.sortIndustriesAlphabetically(this.industries);

    // Update the data source - force refresh by clearing first
    this.dataSource.data = [];
    // Small delay to ensure change detection picks up the empty state
    setTimeout(() => {
      this.dataSource.data = this.industries;
      this.treeControl.dataNodes = this.industries;
      // Restore expanded state
      this.restoreExpandedState();
    }, 0);

    // Clear selection if the deleted item was selected
    if (this.selectedNode && this.selectedNode.id === industryId) {
      this.selectedNode = null;
    }

    // Expand to the parent node if it exists
    if (parentNode) {
      setTimeout(() => {
        this.expandToNewNode(parentNode.id, this.industries);
      }, 10);
    }
  }

  // Method to find a node by its ID
  findNodeById(targetId: string, nodes: Industry[]): Industry | null {
    for (const node of nodes) {
      if (node.id === targetId) {
        return node; // Found the target node
      }
      if (node.children) {
        const result = this.findNodeById(targetId, node.children);
        if (result !== null) {
          return result;
        }
      }
    }
    return null; // Not found
  }

  // Method to find the parent node of a given node ID
  findParentNode(targetId: string, nodes: Industry[], parent: Industry | null = null): Industry | null {
    for (const node of nodes) {
      if (node.id === targetId) {
        return parent; // Found the target, return its parent
      }
      if (node.children) {
        const result = this.findParentNode(targetId, node.children, node);
        if (result !== null) {
          return result;
        }
      }
    }
    return null; // Not found
  }

  onItemSave(event: {item: Industry, newName: string} | {item: Industry, newValue: string, columnKey: string}): void {
    // This method is kept for potential future use but the actual save is handled in saveEdit
  }

  onItemAdd(event: {[key: string]: any}): void {
    // This method is kept for potential future use but the actual add is handled in onCreateIndustry
  }

  // Helper method to save currently expanded nodes
  private saveExpandedState(): void {
    this.expandedNodeIds.clear();
    const getAllNodes = (nodes: Industry[]): Industry[] => {
      const result: Industry[] = [];
      for (const node of nodes) {
        result.push(node);
        if (node.children && node.children.length > 0) {
          result.push(...getAllNodes(node.children));
        }
      }
      return result;
    };

    const allNodes = getAllNodes(this.industries);
    allNodes.forEach(node => {
      if (this.treeControl.isExpanded(node)) {
        this.expandedNodeIds.add(node.id);
      }
    });
  }

  // Helper method to restore expanded state
  private restoreExpandedState(): void {
    if (this.expandedNodeIds.size === 0) {
      return;
    }

    const expandNodes = (nodes: Industry[]): void => {
      for (const node of nodes) {
        if (this.expandedNodeIds.has(node.id)) {
          this.treeControl.expand(node);
        }
        if (node.children && node.children.length > 0) {
          expandNodes(node.children);
        }
      }
    };

    expandNodes(this.industries);
  }
}
